import React, { useState } from "react";
import {
	TextField,
	Button,
	Paper,
	Typography,
	CircularProgress,
	Select,
	MenuItem,
	FormControl,
	Card,
	Box,
	CardContent,
	IconButton,
	Alert,
	InputLabel,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { motion } from "framer-motion";
import { styled } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import {
	useMutation,
	QueryClient,
	QueryClientProvider,
} from "@tanstack/react-query";
import { adminWalletData } from "../../../data";
import axios from "../../../api/axios"; // Your axios instance
import axiosLib from "axios"; // You have both, but prefer using your axios instance
import useAuth from "../../../hooks/useAuth";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

// Styled component for container
const StyledPaper = styled(Paper)(({ theme }) => ({
	padding: theme.spacing(4),
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	justifyContent: "center",
	maxWidth: 400,
	margin: "auto",
	borderRadius: "20px",
	boxShadow: theme.shadows[5],
	transition: "transform 0.2s",
	"&:hover": {
		transform: "scale(1.03)",
	},
}));

// Main withdrawal form component
const CryptoWithdrawalForm = () => {
	// Form states
	const [amount, setAmount] = useState("");
	const [walletAddress, setWalletAddress] = useState("");
	const [selectedWallet, setSelectedWallet] = useState("");
	const [statusMessage, setStatusMessage] = useState("");
	const [errorMessage, setErrorMessage] = useState("");
	const { setAuth, auth, accountMode } = useAuth();
	const axiosPrivate = useAxiosPrivate();
	// API call for withdrawal using axios
	const withdrawCrypto = async (data) => {
		try {
			const response = await axiosPrivate.patch("/withdrawfunds", data);
			return response.data;
		} catch (error) {
			// Handle error: axios error structure
			if (axiosLib.isAxiosError(error)) {
				if (
					error.response &&
					error.response.data &&
					error.response.data.message
				) {
					throw new Error(error.response.data.message);
				} else if (error.message) {
					throw new Error(error.message);
				} else {
					throw new Error("An unknown error occurred");
				}
			} else {
				throw new Error("Network error");
			}
		}
	};

	const mutation = useMutation({
		mutationFn: withdrawCrypto,
		onSuccess: (data) => {
			setStatusMessage("Withdrawal successful!");
			setErrorMessage("");
			// Reset form
			setAmount("");
			setWalletAddress("");
			setSelectedWallet("");
		},
		onError: (error) => {
			setErrorMessage(error.message || "Withdrawal failed");
			setStatusMessage("");
		},
	});

	const handleSubmit = (e) => {
		e.preventDefault();

		// Validation
		if (!amount || isNaN(amount) || Number(amount) <= 0) {
			setErrorMessage("Please enter a valid amount.");
			setStatusMessage("");
			return;
		}
		if (!selectedWallet) {
			setErrorMessage("Please select a wallet.");
			setStatusMessage("");
			return;
		}
		if (!walletAddress || walletAddress.trim() === "") {
			setErrorMessage("Please enter your wallet address.");
			setStatusMessage("");
			return;
		}

		// Wallet address validation (optional, as per your custom logic)
		// const isValidAddress = validateWalletAddress(selectedWallet, walletAddress.trim());
		// if (!isValidAddress) {
		//   setErrorMessage(`Invalid ${selectedWallet} wallet address.`);
		//   setStatusMessage("");
		//   return;
		// }

		// Prepare data
		const data = {
			withdrawalAmount: parseFloat(amount),
			walletType: selectedWallet,
			walletAddr: walletAddress.trim(),
			id: auth?.id,
		};
		// const { id, walletAddr, walletType, withdrawalAmount } = req.body;

		mutation.mutate(data);
	};

	const gotoHome = () => {
		setAuth((prev) => ({ ...prev, homeRoute: true }));
	};

	const realContent = (
		<motion.div
			initial={{ opacity: 0, y: -30 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.5 }}
			className="w-[90%] mx-auto  mt-[7rem]  mb-[4rem]">
			<Button
				onClick={() => {
					gotoHome();
				}}
				sx={{ color: "red", mr: "auto", textDecoration: "underline" }}>
				Back to home{" "}
				<ArrowBackIosIcon sx={{ ml: "0.5rem", fontSize: "1rem" }} />
			</Button>
			<StyledPaper elevation={3}>
				<Typography
					variant="h6"
					component="h6"
					sx={{ fontWeight: "bold" }}
					gutterBottom>
					Withdraw your crypto assets today.
				</Typography>
				<Box sx={{ pl: "1rem", mb: 2 }}>
					<ul className="list-decimal text-gray-500 text-[0.9rem]">
						<li>Enter the amount you wish to withdraw</li>
						<li>Select a crypto wallet from the options below</li>
						<li>Enter your wallet address</li>
						<li>Click on withdraw</li>
					</ul>
				</Box>

				<form onSubmit={handleSubmit} style={{ width: "100%" }}>
					{/* Amount Input */}
					<TextField
						label="Amount in USD"
						variant="outlined"
						fullWidth
						margin="normal"
						required
						type="number"
						inputProps={{ min: 0.01, step: "0.01" }}
						value={amount}
						onChange={(e) => setAmount(e.target.value)}
					/>

					{/* Wallet Selection */}
					<FormControl
						variant="outlined"
						fullWidth
						margin="normal"
						required
						error={!selectedWallet}>
						<InputLabel>Wallet</InputLabel>
						<Select
							value={selectedWallet}
							onChange={(e) => setSelectedWallet(e.target.value)}
							label="Wallet">
							{adminWalletData.map((wlt) => (
								<MenuItem key={wlt.walletAddr} value={wlt.walletType}>
									{wlt.walletType}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					{/* Wallet Address Input */}
					<TextField
						label="Your Wallet Address"
						variant="outlined"
						fullWidth
						margin="normal"
						required
						value={walletAddress}
						onChange={(e) => setWalletAddress(e.target.value)}
					/>

					{/* Submit Button */}
					<Button
						type="submit"
						variant="contained"
						color="primary"
						fullWidth
						disabled={mutation.isLoading}
						style={{ position: "relative", marginTop: "1rem" }}>
						{mutation.isLoading ? (
							<CircularProgress size={24} style={{ position: "absolute" }} />
						) : (
							"Withdraw"
						)}
					</Button>

					{/* Feedback Messages */}
					{statusMessage && (
						<Typography
							color="success.main"
							align="center"
							style={{ marginTop: "16px" }}>
							{statusMessage}
						</Typography>
					)}
					{errorMessage && (
						<Typography
							color="error"
							align="center"
							style={{ marginTop: "16px" }}>
							{errorMessage}
						</Typography>
					)}
				</form>
			</StyledPaper>
		</motion.div>
	);

	const demoContent = (
		<Box
			sx={{
				display: "flex",
				minHeight: "100vh",
				alignItems: "center",
				justifyContent: "center",
				backgroundColor: "#f5f7fa",
				padding: 2,
			}}>
			<Card
				sx={{
					maxWidth: 500,
					width: "100%",
					padding: 4,
					boxShadow: 3,
					borderRadius: 2,
				}}>
				<CardContent sx={{ textAlign: "center" }}>
					<IconButton
						sx={{
							backgroundColor: "#1976d2",
							color: "#fff",
							mb: 2,
							"&:hover": {
								backgroundColor: "#1565c0",
							},
						}}
						aria-label="info">
						<InfoOutlinedIcon fontSize="large" />
					</IconButton>
					<Typography variant="h5" component="h2" gutterBottom>
						Withdrawal Not Available for Demo Funds
					</Typography>
					<Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />}>
						<Typography variant="body1" sx={{ mt: 1 }}>
							Since this is a demo account, withdrawal functionality is
							disabled. Please upgrade to a real account to access this feature.
						</Typography>
					</Alert>
				</CardContent>
			</Card>
		</Box>
	);

	const activeContent = accountMode === "real" ? realContent : demoContent;

	return activeContent;
};

// Theme setup
const theme = createTheme({
	palette: {
		primary: {
			main: "#1976d2",
		},
		secondary: {
			main: "#dc004e",
		},
	},
});

// Initialize React Query Client
const queryClient = new QueryClient();

// Wrapper component for provider
export default function CryptoWithdrawal() {
	return (
		<QueryClientProvider client={queryClient}>
			<ThemeProvider theme={theme}>
				<CssBaseline />
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						height: "100vh",
						backgroundColor: "#f5f5f5",
					}}>
					<CryptoWithdrawalForm />
				</div>
			</ThemeProvider>
		</QueryClientProvider>
	);
}
